import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import CourseCard from "../components/common/CourseCard";
import { UniversityDetailSkeleton } from "../components/common/PublicPageSkeletons";
import { universityService } from "../services/api/universityService";
import type { Course } from "../types/course";
import type { University } from "../types/university";

const UniversityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [university, setUniversity] = useState<University | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchUniversityData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch university details and courses in parallel
      const [universityResponse, coursesResponse] = await Promise.all([
        universityService.getUniversityDetails(id!),
        universityService.getUniversityPrograms(id!),
      ]);

      if (universityResponse.data && universityResponse.data.success) {
        setUniversity(universityResponse.data.data);
      }

      if (coursesResponse.data && coursesResponse.data.success) {
        const coursesData = coursesResponse.data.data as any;
        setCourses(coursesData.courses || []);
      }
    } catch (err) {
      console.error("Error fetching university data:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch university data"
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      navigate("/universities");
      return;
    }

    fetchUniversityData();
  }, [id, navigate, fetchUniversityData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredCourses = courses.filter(
    (course) =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.degree.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.level.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <UniversityDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={fetchUniversityData}
              className="w-full bg-cyan-600 text-white px-6 py-2 rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Try Again
            </button>
            <Link
              to="/universities"
              className="block w-full bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Back to Universities
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50">
      {/* University Header */}
      <section
        className="relative text-white py-16"
        style={university?.bannerUrl ? { backgroundImage: `url(${university.bannerUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
      >
        {university?.bannerUrl ? (
          <div className="absolute inset-0 bg-black/55" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600" />
        )}
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <Link
              to="/universities"
              className="inline-flex items-center text-cyan-100 hover:text-white transition-colors mb-6"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Universities
            </Link>

            {/* University Info */}
            <div className="flex items-start space-x-6">
              <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                {university?.logoUrl ? (
                  <img
                    src={university.logoUrl}
                    alt={university.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <span className="text-white font-bold text-2xl">
                    {university?.name.charAt(0)}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  {university?.name}
                </h1>
                <div className="flex items-center text-cyan-100 mb-4">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    {university?.city}, {university?.country}
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 inline-block">
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-300">
                        {courses.length}
                      </div>
                      <div className="text-cyan-100 text-sm">Programs</div>
                    </div>
                    {university?.description && (
                      <div className="text-cyan-100 max-w-md">
                        {university.description}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="bg-white shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search programs by name, degree, or level..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-lg"
              />
            </div>
            <div className="mt-4 flex justify-between items-center text-sm text-gray-600">
              <div>
                Showing {filteredCourses.length} of {courses.length} programs
              </div>
              {searchTerm && (
                <div className="flex items-center">
                  <span className="px-3 py-1 bg-cyan-100 text-cyan-800 rounded-full text-xs">
                    Search: "{searchTerm}"
                  </span>
                  <button
                    onClick={() => setSearchTerm("")}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {filteredCourses.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 max-w-md mx-auto">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {searchTerm ? "No Programs Found" : "No Programs Available"}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {searchTerm
                      ? "Try adjusting your search criteria."
                      : "This university doesn't have any programs listed yet."}
                  </p>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredCourses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    university={university || undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default UniversityDetail;
